import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Search, Upload, Wand2, Edit3, Check, SlidersHorizontal, Eye, EyeOff } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import ImageUpload, { resolveImageUrl } from '../components/ImageUpload';
import MobileListCard from '../components/MobileListCard';
import BulkProductUpload from '../components/BulkProductUpload';
import BulkProductEdit from '../components/BulkProductEdit';
import ProductFiltersDrawer, { ActiveFilterChips, applyFilters, activeFilterCount, EMPTY_FILTERS } from '../components/ProductFiltersDrawer';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import Rial from '../components/Rial';

const empty = (defaultSlug = '') => ({
  name: '',
  category_slug: defaultSlug,
  sku: '',
  size: '',
  size_type: 'size',
  price: 0,
  tag: '',
  tag_color: '',
  image: '',
  in_stock: true,
  stock_status: 'in_stock',
  vat_applicable: false,
  uom: 'ctn',
  min_qty: 1,
  is_visible: true,
  custom_attrs: {},
  // Best-before / expiry date as ISO ("YYYY-MM-DD") — matches the
  // <input type="date"> value format so the field wires up cleanly.
  // Empty means "not tracked" and renders as em-dash in the report.
  expiry_date: '',
  // Per-batch inventory. Each entry: { batch_ref, production_date,
  // expiry_date, qty }. Admin can add / remove rows here; the bulk
  // uploader creates them automatically. When populated, stock_qty
  // is auto-derived as the sum of batch qtys server-side.
  batches: [],
});

const TAG_PRESETS = ['New', 'Best Seller', 'Premium', 'Value', 'Bundle', 'Bulk', 'Family', 'Spicy', 'Popular'];

const AdminProducts = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const { can } = useAdminPermissions();
  // Cache the action checks the page reads frequently. Keeps the JSX
  // tidy and makes the gating intent crystal-clear at the call site.
  const canCreate = can('products.create');
  const canEdit = can('products.edit');
  const canDelete = can('products.delete');
  const canBulkEdit = can('products.bulk_edit');
  const canBulkDelete = can('products.bulk_delete');
  const canBulkUpload = can('products.bulk_upload');
  const canPhotoUpload = can('products.photo_upload');
  const canStockToggle = can('products.stock_toggle');
  // Field-visibility flags — when missing, the column/cell/form-field
  // for that attribute is hidden from this admin.
  const showPrice    = can('products.field_price');
  const showTag      = can('products.field_tag');
  const showAttrs    = can('products.field_attrs');
  const showVat      = can('products.field_vat');
  const showSize     = can('products.field_size');
  const showUom      = can('products.field_uom');
  const showMinQty   = can('products.field_min_qty');
  const canEditSku   = can('products.sku_edit');
  // Show the SKU column / form field when EITHER:
  //   - the admin has explicit field visibility (`products.field_sku`), OR
  //   - the admin has SKU-edit permission (edit implies view — otherwise
  //     granting `sku_edit` alone would silently fail because the column
  //     would still be hidden).
  const showSku      = can('products.field_sku') || canEditSku;
  const showImage    = can('products.field_image');
  const showCategory = can('products.field_category');
  const showInStock  = can('products.field_in_stock');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | product
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  // True while the inline ImageUpload is busy posting to /api/admin/upload
  // (esp. when fal.ai bg-removal is checked, this can take ~7 s). We need
  // to block the Save/Create button during that window — otherwise the
  // admin clicks Save before the new image URL lands in `form.image` and
  // the product saves with the OLD url, which feels like "save failed".
  const [imageUploading, setImageUploading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);   // ids checked in the table
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  // Inline-edit mode: when true, every editable cell turns into a click-to-
  // edit input/select/toggle. Each cell saves directly to the backend via
  // PUT /api/admin/products/{id} on blur or Enter (optimistic UI — local
  // state is updated immediately, reverted on error).
  const [editMode, setEditMode] = useState(false);
  const [savingCell, setSavingCell] = useState(null);    // `${id}:${field}` while in-flight
  // Inline-edit (Edit mode) requires the same write permission as a
  // single-row PUT to /admin/products/{id}. A sub-admin who can see the
  // Edit mode button (e.g. because they hold products.stock_toggle)
  // should NOT be able to inline-edit name/price/category/etc. without
  // the broader products.edit slug — the backend rejects those writes
  // anyway, this just hides the affordance so the UI matches reality.
  const editAllowed = editMode && canEdit;
  // SKU has its own dedicated permission (`products.sku_edit`) so an admin
  // can be granted edit access for ONLY the SKU column without unlocking
  // every other field. Falls back to the standard `editAllowed` so the
  // existing "you have edit perm too" path still works.
  const skuEditAllowed = editMode && canEditSku;

  const load = async () => {
    setLoading(true);
    try {
      const [pr, cr, me] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers: authHeaders }),
        axios.get(`${API}/admin/categories`, { headers: authHeaders }),
        axios.get(`${API}/admin/me`, { headers: authHeaders }),
      ]);
      // Per-admin category scope: when the signed-in admin has a
      // non-empty `category_slugs` list, restrict BOTH the products
      // list AND the categories shown in the create/edit form to that
      // scope. The backend mirrors the restriction on writes — this
      // is purely a usability filter so the admin only sees what
      // they're allowed to manage.
      const scope = Array.isArray(me.data?.category_slugs) ? me.data.category_slugs : [];
      const isScoped = scope.length > 0 && !me.data?.is_super_admin;
      const filteredCats = isScoped ? cr.data.filter((c) => scope.includes(c.slug)) : cr.data;
      const filteredProds = isScoped ? pr.data.filter((p) => scope.includes(p.category_slug)) : pr.data;
      setProducts(filteredProds);
      setCategories(filteredCats);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setForm(empty(categories[0]?.slug || ''));
    setEditing('new');
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      category_slug: p.category_slug,
      sku: p.sku || '',
      size: p.size || '',
      size_type: p.size_type || 'size',
      price: p.price,
      tag: p.tag || '',
      tag_color: p.tag_color || '',
      image: p.image || '',
      in_stock: p.in_stock,
      stock_status: p.stock_status || (p.in_stock ? 'in_stock' : 'out_of_stock'),
      vat_applicable: p.vat_applicable || false,
      uom: p.uom || 'ctn',
      min_qty: p.min_qty ?? 1,
      is_visible: p.is_visible !== false,
      custom_attrs: p.custom_attrs || {},
      expiry_date: (p.expiry_date || '').slice(0, 10),
      batches: (p.batches || []).map((b) => ({
        batch_ref: b.batch_ref || '',
        production_date: (b.production_date || '').slice(0, 10),
        expiry_date: (b.expiry_date || '').slice(0, 10),
        qty: Number(b.qty || 0),
      })),
    });
    setEditing(p);
  };

  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        min_qty: Math.max(1, parseInt(form.min_qty, 10) || 1),
        uom: (form.uom || 'ctn').trim() || 'ctn',
        expiry_date: (form.expiry_date || '').trim() || '',
        // Strip out empty-string batches (admin added a row and left
        // everything blank) so we don't persist zero-info batches.
        batches: (form.batches || [])
          .filter((b) => (b.batch_ref || b.production_date || b.expiry_date || Number(b.qty) > 0))
          .map((b) => ({
            batch_ref: (b.batch_ref || '').trim(),
            production_date: (b.production_date || '').trim(),
            expiry_date: (b.expiry_date || '').trim(),
            qty: Math.max(0, parseInt(b.qty, 10) || 0),
          })),
      };
      if (editing === 'new') {
        await axios.post(`${API}/admin/products`, payload, {
          headers: authHeaders,
          timeout: 30000, // never let a stuck request hang the dialog
        });
        toast({ title: 'Product created', description: payload.name });
      } else {
        await axios.put(`${API}/admin/products/${editing.id}`, payload, {
          headers: authHeaders,
          timeout: 30000,
        });
        toast({ title: 'Product updated', description: payload.name });
      }
      close();
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers: authHeaders });
      toast({ title: 'Product deleted', description: p.name });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const filtered = useMemo(() => {
    let list = filter === 'all' ? products : products.filter((p) => p.category_slug === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const attrBlob = Object.values(p.custom_attrs || {}).join(' ').toLowerCase();
        return (
          (p.name || '').toLowerCase().includes(q) ||
          (p.sku || '').toLowerCase().includes(q) ||
          (p.size || '').toLowerCase().includes(q) ||
          (p.tag || '').toLowerCase().includes(q) ||
          (p.id || '').toLowerCase().includes(q) ||
          (p.category_slug || '').toLowerCase().includes(q) ||
          attrBlob.includes(q)
        );
      });
    }
    // Custom facet filters from the drawer — applied last so they compose
    // with the active category pill + search box (AND across all three).
    list = applyFilters(list, filters);
    return list;
  }, [products, filter, search, filters]);

  // Distinct UOM + Tag values fed to the filters drawer so admins only see
  // values that actually exist in the catalog (not a hardcoded preset list).
  const uomOptions = useMemo(
    () => Array.from(new Set(products.map((p) => p.uom || 'ctn'))).sort(),
    [products],
  );
  const tagOptions = useMemo(
    () => Array.from(new Set(products.map((p) => p.tag || ''))).sort(),
    [products],
  );
  const filterChips = activeFilterCount(filters);

  // Selection helpers — only ids that are still in the filtered view count as
  // "visible selection", which keeps the master checkbox state consistent
  // when the admin changes filter/search after selecting rows.
  const visibleIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const visibleSelectedIds = useMemo(
    () => selectedIds.filter((id) => visibleIds.includes(id)),
    [selectedIds, visibleIds],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleSelectedIds.length === visibleIds.length;
  const someVisibleSelected = visibleSelectedIds.length > 0 && !allVisibleSelected;

  const toggleRow = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAllVisible = () =>
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id));
      // union: keep prior selections + add visible ones
      return Array.from(new Set([...prev, ...visibleIds]));
    });
  const clearSelection = () => setSelectedIds([]);

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} product${selectedIds.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/products/bulk-delete`,
        { ids: selectedIds },
        { headers: authHeaders },
      );
      toast({ title: 'Products deleted', description: `${r.data.deleted} of ${r.data.requested} removed.` });
      clearSelection();
      load();
    } catch (err) {
      toast({ title: 'Bulk delete failed', description: err.response?.data?.detail || 'Try again' });
    }
  };

  // Inline cell save — used by every editable cell in edit mode. Optimistic:
  // we patch local state immediately, fire the PUT, and rollback + toast on
  // error. Only sends the changed field, never the whole product, so
  // concurrent edits to other fields by another admin don't get clobbered.
  const saveField = async (product, field, value) => {
    if (product[field] === value) return;
    const cellKey = `${product.id}:${field}`;
    setSavingCell(cellKey);
    // Optimistic local update
    setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, [field]: value } : p)));
    try {
      await axios.put(
        `${API}/admin/products/${product.id}`,
        { [field]: value },
        { headers: authHeaders },
      );
    } catch (err) {
      // Rollback on failure
      setProducts((list) => list.map((p) => (p.id === product.id ? { ...p, [field]: product[field] } : p)));
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again' });
    } finally {
      setSavingCell((cur) => (cur === cellKey ? null : cur));
    }
  };

  // Save a single custom-attribute key on a product (e.g. brand="Pepsi").
  // Empty value removes the key. Other attribute keys are preserved.
  const saveAttr = async (product, key, value) => {
    const prevAttrs = product.custom_attrs || {};
    if ((prevAttrs[key] || '') === (value || '')) return;
    const nextAttrs = { ...prevAttrs };
    if (value) nextAttrs[key] = value;
    else delete nextAttrs[key];
    const cellKey = `${product.id}:attr:${key}`;
    setSavingCell(cellKey);
    setProducts((list) =>
      list.map((p) => (p.id === product.id ? { ...p, custom_attrs: nextAttrs } : p)),
    );
    try {
      await axios.put(
        `${API}/admin/products/${product.id}`,
        { custom_attrs: nextAttrs },
        { headers: authHeaders },
      );
    } catch (err) {
      setProducts((list) =>
        list.map((p) => (p.id === product.id ? { ...p, custom_attrs: prevAttrs } : p)),
      );
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again' });
    } finally {
      setSavingCell((cur) => (cur === cellKey ? null : cur));
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">
              Products
            </h1>
            <p className="text-neutral-500 mt-1">Add, edit, or remove items from your catalog.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Edit mode shows the inline-edit cells. Gate it behind any
                permission that actually unlocks an inline-edit cell —
                otherwise the toggle would do nothing useful. */}
            {(canEdit || canStockToggle || canEditSku) && (
              <Button
                onClick={() => setEditMode((v) => !v)}
                variant={editMode ? 'default' : 'outline'}
                className={`h-11 px-5 rounded-full font-semibold ${
                  editMode
                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500'
                    : 'border-emerald-500 text-emerald-700 hover:bg-emerald-50'
                }`}
                data-testid="edit-mode-toggle"
              >
                {editMode ? (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Done editing
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4 mr-1" /> Edit mode
                  </>
                )}
              </Button>
            )}
            {canBulkUpload && (
              <Button
                onClick={() => setBulkOpen(true)}
                variant="outline"
                className="h-11 px-5 rounded-full font-semibold border-[#0078D4] text-[#0078D4] hover:bg-[#DEECF9]"
                data-testid="bulk-upload-open"
              >
                <Upload className="w-4 h-4 mr-1" /> Bulk upload
              </Button>
            )}
            {canCreate && (
              <Button
                onClick={openNew}
                className="h-11 px-5 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold btn-lift"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add product
              </Button>
            )}
          </div>
        </div>

        {/* Search bar + Filters button */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, size, tag or division…"
              className="pl-9 pr-9 h-10 rounded-full"
              data-testid="product-search"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                data-testid="product-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            onClick={() => setFiltersOpen(true)}
            variant="outline"
            className="h-10 rounded-full border-neutral-300 hover:border-[#0078D4] hover:text-[#0078D4]"
            data-testid="open-filters"
          >
            <SlidersHorizontal className="w-4 h-4 mr-1.5" />
            Filters
            {filterChips > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#0078D4] text-white text-[10px] font-bold" data-testid="filters-badge">
                {filterChips}
              </span>
            )}
          </Button>
        </div>

        {/* Active filter chips — surface what's filtering the table so admins
            know exactly why they see N rows and can dismiss one facet at a time */}
        {filterChips > 0 && (
          <div className="mb-4">
            <ActiveFilterChips value={filters} onChange={setFilters} />
          </div>
        )}

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
              filter === 'all' ? 'bg-[#0F172A] text-white' : 'bg-white border border-neutral-200 text-neutral-700'
            }`}
          >
            All ({products.length})
          </button>
          {categories.map((c) => {
            const count = products.filter((p) => p.category_slug === c.slug).length;
            return (
              <button
                key={c.slug}
                onClick={() => setFilter(c.slug)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                  filter === c.slug ? 'text-white' : 'bg-white border border-neutral-200 text-neutral-700'
                }`}
                style={filter === c.slug ? { background: c.color } : {}}
              >
                {c.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-neutral-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-neutral-500" data-testid="product-empty">
              {search ? `No products match "${search}".` : 'No products yet.'}
            </div>
          ) : (
            <>
              {/* Mobile card list — Feb 2026 native-app pass */}
              <div className="md:hidden p-3 space-y-2" data-testid="admin-products-list-mobile">
                {filtered.map((p) => {
                  const cat = categories.find((c) => c.slug === p.category_slug);
                  const imgSrc = resolveImageUrl(p.image) || resolveImageUrl(cat?.image);
                  return (
                    <MobileListCard
                      key={p.id}
                      testId={`admin-product-card-${p.id}`}
                      title={
                        <div className="flex items-center gap-2 min-w-0">
                          {imgSrc && (
                            <span
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md overflow-hidden shrink-0"
                              style={{ background: cat?.bg || '#F8FAFC' }}
                            >
                              <img src={imgSrc} alt={p.name} className="max-w-full max-h-full object-contain" />
                            </span>
                          )}
                          <span className="truncate">{p.name}</span>
                        </div>
                      }
                      statusPill={
                        p.is_visible === false ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-neutral-100 border border-neutral-200 text-neutral-500 inline-flex items-center gap-1">
                            <EyeOff className="w-3 h-3" /> Hidden
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
                            Visible
                          </span>
                        )
                      }
                      businessName={cat?.name || '—'}
                      customerName={p.sku ? `#${p.sku}` : ''}
                      amount={
                        p.price != null
                          ? `OMR ${Number(p.price).toFixed(3)}`
                          : '—'
                      }
                      date={
                        p.pack_size ? `Pack ${p.pack_size}` : p.uom ? p.uom : ''
                      }
                      onClick={editAllowed ? () => openEdit(p) : undefined}
                      actions={[
                        ...(editAllowed
                          ? [{ label: 'Edit', icon: Pencil, onClick: () => openEdit(p), testId: `edit-product-mobile-${p.id}` }]
                          : []),
                        ...(canDelete
                          ? [
                              { divider: true },
                              { label: 'Delete', icon: Trash2, onClick: () => remove(p), danger: true, testId: `delete-product-mobile-${p.id}` },
                            ]
                          : []),
                      ]}
                    />
                  );
                })}
              </div>

              {/* Desktop table — untouched, hidden on <md */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someVisibleSelected;
                        }}
                        onChange={toggleAllVisible}
                        className="w-4 h-4 rounded border-neutral-300 text-[#0078D4] focus:ring-[#0078D4] cursor-pointer"
                        aria-label="Select all visible products"
                        data-testid="select-all-products"
                      />
                    </th>
                    {showImage && <th className="px-6 py-3 w-16"></th>}
                    {showSku && <th className="px-6 py-3">Item Code</th>}
                    <th className="px-6 py-3">Name</th>
                    {showCategory && <th className="px-6 py-3">Division</th>}
                    {showSize && <th className="px-6 py-3">Pack / Size</th>}
                    {showUom && <th className="px-6 py-3">UOM</th>}
                    {showMinQty && <th className="px-6 py-3">Min Qty</th>}
                    {showPrice && <th className="px-6 py-3">Price</th>}
                    {showTag && <th className="px-6 py-3">Tag</th>}
                    {showAttrs && <th className="px-6 py-3 min-w-[200px]">Attributes</th>}
                    {showVat && <th className="px-6 py-3">VAT</th>}
                    {showInStock && <th className="px-6 py-3">Stock</th>}
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const cat = categories.find((c) => c.slug === p.category_slug);
                    const imgSrc = resolveImageUrl(p.image) || resolveImageUrl(cat?.image);
                    return (
                      <tr key={p.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${selectedIds.includes(p.id) ? 'bg-[#F7FBFF]' : ''}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(p.id)}
                            onChange={() => toggleRow(p.id)}
                            className="w-4 h-4 rounded border-neutral-300 text-[#0078D4] focus:ring-[#0078D4] cursor-pointer"
                            aria-label={`Select ${p.name}`}
                            data-testid={`select-product-${p.id}`}
                          />
                        </td>
                        {showImage && (
                          <td className="px-6 py-3">
                            <div
                              className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden"
                              style={{ background: cat?.bg || '#F8FAFC' }}
                            >
                              {imgSrc && (
                                <img src={imgSrc} alt={p.name} className="max-w-full max-h-full object-contain" />
                              )}
                            </div>
                          </td>
                        )}
                        {showSku && (
                        <td className="px-6 py-3.5 font-mono text-xs text-neutral-700">
                          {skuEditAllowed ? (
                            <EditableText
                              value={p.sku || ''}
                              onSave={(v) => saveField(p, 'sku', (v || '').trim())}
                              saving={savingCell === `${p.id}:sku`}
                              placeholder="—"
                              testId={`edit-sku-${p.id}`}
                              className="font-mono"
                            />
                          ) : (
                            p.sku || '—'
                          )}
                        </td>
                        )}
                        <td className="px-6 py-3.5 font-medium">
                          {editAllowed ? (
                            <EditableText
                              value={p.name}
                              onSave={(v) => saveField(p, 'name', v)}
                              saving={savingCell === `${p.id}:name`}
                              testId={`edit-name-${p.id}`}
                              className="font-medium"
                            />
                          ) : (
                            p.name
                          )}
                          {p.is_visible === false && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-neutral-200 text-neutral-600 text-[10px] font-bold uppercase tracking-wide align-middle" data-testid={`hidden-pill-${p.id}`}>
                              <EyeOff className="w-3 h-3" /> Hidden
                            </span>
                          )}
                        </td>
                        {showCategory && (
                        <td className="px-6 py-3.5">
                          {editAllowed ? (
                            <select
                              value={p.category_slug}
                              onChange={(e) => saveField(p, 'category_slug', e.target.value)}
                              disabled={savingCell === `${p.id}:category_slug`}
                              className="h-8 px-2 rounded-md border border-neutral-300 bg-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
                              data-testid={`edit-category-${p.id}`}
                            >
                              {categories.map((c) => (
                                <option key={c.slug} value={c.slug}>{c.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className="px-2 py-0.5 rounded-full text-[11px] font-semibold text-white"
                              style={{ background: cat?.color || '#888' }}
                            >
                              {cat?.name || p.category_slug}
                            </span>
                          )}
                        </td>
                        )}
                        {showSize && (
                        <td className="px-6 py-3.5 text-neutral-600">
                          {editAllowed ? (
                            <EditableText
                              value={p.size || ''}
                              onSave={(v) => saveField(p, 'size', v)}
                              saving={savingCell === `${p.id}:size`}
                              placeholder="—"
                              testId={`edit-size-${p.id}`}
                            />
                          ) : (
                            p.size || '—'
                          )}
                        </td>
                        )}
                        {showUom && (
                        <td className="px-6 py-3.5 text-neutral-600 font-medium">
                          {editAllowed ? (
                            <EditableText
                              value={p.uom || 'ctn'}
                              onSave={(v) => saveField(p, 'uom', (v || 'ctn').trim() || 'ctn')}
                              saving={savingCell === `${p.id}:uom`}
                              testId={`edit-uom-${p.id}`}
                              className="font-medium"
                            />
                          ) : (
                            p.uom || 'ctn'
                          )}
                        </td>
                        )}
                        {showMinQty && (
                        <td className="px-6 py-3.5 text-neutral-600">
                          {editAllowed ? (
                            <EditableText
                              value={String(p.min_qty ?? 1)}
                              onSave={(v) => saveField(p, 'min_qty', Math.max(1, parseInt(v, 10) || 1))}
                              saving={savingCell === `${p.id}:min_qty`}
                              type="number"
                              testId={`edit-minqty-${p.id}`}
                            />
                          ) : (
                            p.min_qty ?? 1
                          )}
                        </td>
                        )}
                        {showPrice && (
                        <td className="px-6 py-3.5 font-semibold">
                          {editAllowed ? (
                            <EditableText
                              value={p.price.toFixed(3)}
                              onSave={(v) => {
                                const n = parseFloat(v);
                                if (!Number.isFinite(n) || n < 0) return;
                                saveField(p, 'price', Math.round(n * 1000) / 1000);
                              }}
                              saving={savingCell === `${p.id}:price`}
                              type="number"
                              step="0.001"
                              prefix={<Rial />}
                              testId={`edit-price-${p.id}`}
                              className="font-semibold"
                            />
                          ) : (
                            <><Rial /> {p.price.toFixed(3)}</>
                          )}
                        </td>
                        )}
                        {showTag && (
                        <td className="px-6 py-3.5 text-neutral-600">
                          {editAllowed ? (
                            <EditableText
                              value={p.tag || ''}
                              onSave={(v) => saveField(p, 'tag', v)}
                              saving={savingCell === `${p.id}:tag`}
                              placeholder="—"
                              testId={`edit-tag-${p.id}`}
                            />
                          ) : (
                            p.tag || '—'
                          )}
                        </td>
                        )}
                        {showAttrs && (
                        <td className="px-6 py-3.5 min-w-[200px]">
                          {(() => {
                            const filters = cat?.custom_filters || [];
                            if (filters.length === 0) {
                              return (
                                <span className="text-neutral-400 text-xs italic" data-testid={`attrs-none-${p.id}`}>
                                  No schema
                                </span>
                              );
                            }
                            const attrs = p.custom_attrs || {};
                            return (
                              <div className="flex flex-col gap-1.5" data-testid={`attrs-cell-${p.id}`}>
                                {filters.map((f) => {
                                  const value = attrs[f.key] || '';
                                  const saving = savingCell === `${p.id}:attr:${f.key}`;
                                  if (editAllowed) {
                                    return (
                                      <div key={f.key} className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide min-w-[52px]">
                                          {f.label}
                                        </span>
                                        <select
                                          value={value}
                                          onChange={(e) => saveAttr(p, f.key, e.target.value)}
                                          disabled={saving}
                                          className={`h-7 px-2 rounded-md border border-neutral-300 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-[#0078D4] ${
                                            saving ? 'opacity-50' : ''
                                          }`}
                                          data-testid={`edit-attr-${p.id}-${f.key}`}
                                        >
                                          <option value="">—</option>
                                          {(f.options || []).map((o) => (
                                            <option key={o.id || o.value || o.label} value={o.value || o.label}>
                                              {o.label || o.value}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  }
                                  if (!value) {
                                    return (
                                      <div key={f.key} className="flex items-center gap-1.5 text-xs">
                                        <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide min-w-[52px]">
                                          {f.label}
                                        </span>
                                        <span className="text-neutral-400">—</span>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={f.key} className="flex items-center gap-1.5 text-xs">
                                      <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wide min-w-[52px]">
                                        {f.label}
                                      </span>
                                      <span
                                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#EFF6FC] text-[#0E4A7B] text-[11px] font-semibold border border-[#DEECF9]"
                                        data-testid={`attr-value-${p.id}-${f.key}`}
                                      >
                                        {value}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </td>
                        )}
                        {showVat && (
                        <td className="px-6 py-3.5">
                          {editAllowed ? (
                            <button
                              onClick={() => saveField(p, 'vat_applicable', !p.vat_applicable)}
                              disabled={savingCell === `${p.id}:vat_applicable`}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${
                                p.vat_applicable
                                  ? 'bg-[#DEECF9] text-[#0078D4] hover:bg-[#BBDCF7]'
                                  : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                              }`}
                              data-testid={`edit-vat-${p.id}`}
                            >
                              {p.vat_applicable ? '5%' : '—'}
                            </button>
                          ) : p.vat_applicable ? (
                            <span className="px-2 py-0.5 rounded-full bg-[#DEECF9] text-[#0078D4] text-[11px] font-semibold">
                              5%
                            </span>
                          ) : (
                            <span className="text-neutral-400 text-xs">—</span>
                          )}
                        </td>
                        )}
                        {showInStock && (
                        <td className="px-6 py-3.5">
                          {editMode && canStockToggle ? (
                            <button
                              onClick={() => saveField(p, 'in_stock', !p.in_stock)}
                              disabled={savingCell === `${p.id}:in_stock`}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${
                                p.in_stock
                                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                              data-testid={`edit-stock-${p.id}`}
                            >
                              {p.in_stock ? 'In stock' : 'Out'}
                            </button>
                          ) : p.in_stock ? (
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
                              In stock
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold">
                              Out
                            </span>
                          )}
                        </td>
                        )}
                        <td className="px-6 py-3.5 text-right">
                          {canEdit && (
                            <button
                              onClick={() => saveField(p, 'is_visible', p.is_visible === false)}
                              disabled={savingCell === `${p.id}:is_visible`}
                              className={`p-2 rounded-md transition-colors ${
                                p.is_visible === false
                                  ? 'text-neutral-400 hover:bg-neutral-100'
                                  : 'text-[#0078D4] hover:bg-[#DEECF9]'
                              }`}
                              title={p.is_visible === false ? 'Hidden from storefront — click to show' : 'Visible on storefront — click to hide'}
                              data-testid={`toggle-visibility-${p.id}`}
                            >
                              {p.is_visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => openEdit(p)} className="p-2 hover:bg-neutral-100 rounded-md text-neutral-700" data-testid={`edit-product-${p.id}`}>
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => remove(p)} className="p-2 hover:bg-red-50 rounded-md text-red-500" data-testid={`delete-product-${p.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
            <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editing === 'new' ? 'Add product' : 'Edit product'}</h3>
              <button onClick={close} className="p-2 hover:bg-neutral-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Name</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
              </div>
              {showSku && (
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">
                  SKU / Item Code <span className="text-neutral-400 normal-case font-normal">— used by inventory upload</span>
                </label>
                <Input
                  value={form.sku || ''}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  placeholder="e.g. SKU-0001"
                  className="mt-1 font-mono"
                  data-testid="product-sku-input"
                  disabled={!canEditSku}
                  readOnly={!canEditSku}
                />
                {!canEditSku && (
                  <p className="text-[11px] text-neutral-400 mt-1 italic">Read-only — Item Code editing is restricted for your role.</p>
                )}
              </div>
              )}
              {showCategory && (
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Division</label>
                <select
                  value={form.category_slug}
                  onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm"
                >
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {showSize && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">
                    {form.size_type === 'pack' ? 'Pack' : 'Size'}
                  </label>
                  <div className="mt-1 flex items-stretch gap-1.5">
                    <select
                      value={form.size_type || 'size'}
                      onChange={(e) => setForm({ ...form, size_type: e.target.value })}
                      className="h-10 px-2 rounded-md border border-neutral-300 bg-neutral-50 text-xs font-semibold uppercase tracking-wide text-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                      data-testid="product-size-type-select"
                      aria-label="Size or pack"
                    >
                      <option value="size">Size</option>
                      <option value="pack">Pack</option>
                    </select>
                    <Input
                      value={form.size}
                      onChange={(e) => setForm({ ...form, size: e.target.value })}
                      placeholder={form.size_type === 'pack' ? 'e.g. 12 x 500g' : 'e.g. 100ml'}
                      className="flex-1"
                      data-testid="product-size-input"
                    />
                  </div>
                </div>
                )}
                {showPrice && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Price (<Rial />)</label>
                  <Input type="number" step="0.001" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" />
                </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {showUom && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Unit (UOM)</label>
                  <Input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} placeholder="ctn" className="mt-1" data-testid="product-uom-input" />
                  <p className="text-[11px] text-neutral-500 mt-1">Default: ctn (carton)</p>
                </div>
                )}
                {showMinQty && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Min order qty</label>
                  <Input type="number" min="1" step="1" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: e.target.value })} className="mt-1" data-testid="product-minqty-input" />
                  <p className="text-[11px] text-neutral-500 mt-1">Minimum cartons per order</p>
                </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Expiry / best-before</label>
                  <Input
                    type="date"
                    value={form.expiry_date || ''}
                    onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                    className="mt-1"
                    data-testid="product-expiry-input"
                  />
                  <p className="text-[11px] text-neutral-500 mt-1">Rendered as a colour-coded pill on the live stock report (opt-in column).</p>
                </div>
              </div>
              {/* Per-batch inventory editor. Each row tracks one production
                  run — the bulk uploader creates these automatically; this
                  UI lets admin verify / touch up manually. Sum of qtys
                  auto-becomes the product's stock_qty on save. */}
              <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50/50">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Batches / lots</label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs rounded-full border-neutral-300 hover:border-[#0078D4] hover:text-[#0078D4]"
                    onClick={() => setForm({
                      ...form,
                      batches: [...(form.batches || []), { batch_ref: '', production_date: '', expiry_date: '', qty: 0 }],
                    })}
                    data-testid="product-batch-add"
                  >
                    + Add batch
                  </Button>
                </div>
                {(form.batches || []).length === 0 ? (
                  <p className="text-[11px] text-neutral-400 italic py-2">
                    No batches tracked. Add one manually or upload via the bulk template (Item Code + Batch Ref + Production Date + Expiry Date + Qty per row).
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.batches.map((b, i) => (
                      <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1fr_0.6fr_auto] gap-2 items-end" data-testid={`product-batch-row-${i}`}>
                        <div>
                          <label className="text-[10px] font-semibold text-neutral-500 uppercase">Batch Ref</label>
                          <Input
                            value={b.batch_ref || ''}
                            placeholder="LOT-A"
                            onChange={(e) => {
                              const next = [...form.batches];
                              next[i] = { ...next[i], batch_ref: e.target.value };
                              setForm({ ...form, batches: next });
                            }}
                            className="h-9 text-sm"
                            data-testid={`product-batch-ref-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-neutral-500 uppercase">Production</label>
                          <Input
                            type="date"
                            value={b.production_date || ''}
                            onChange={(e) => {
                              const next = [...form.batches];
                              next[i] = { ...next[i], production_date: e.target.value };
                              setForm({ ...form, batches: next });
                            }}
                            className="h-9 text-sm"
                            data-testid={`product-batch-prod-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-neutral-500 uppercase">Expiry</label>
                          <Input
                            type="date"
                            value={b.expiry_date || ''}
                            onChange={(e) => {
                              const next = [...form.batches];
                              next[i] = { ...next[i], expiry_date: e.target.value };
                              setForm({ ...form, batches: next });
                            }}
                            className="h-9 text-sm"
                            data-testid={`product-batch-exp-${i}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-neutral-500 uppercase">Qty</label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={b.qty}
                            onChange={(e) => {
                              const next = [...form.batches];
                              next[i] = { ...next[i], qty: e.target.value };
                              setForm({ ...form, batches: next });
                            }}
                            className="h-9 text-sm"
                            data-testid={`product-batch-qty-${i}`}
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-9 px-2 text-xs rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300"
                          onClick={() => setForm({ ...form, batches: form.batches.filter((_, j) => j !== i) })}
                          data-testid={`product-batch-remove-${i}`}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <div className="pt-1 text-[11px] text-neutral-500 border-t border-neutral-200">
                      Total on hand across batches: <strong className="text-neutral-800">
                        {(form.batches || []).reduce((sum, b) => sum + (parseInt(b.qty, 10) || 0), 0)}
                      </strong>
                      &nbsp;· auto-populates <code>stock_qty</code> on save. Nearest-expiry batch drives the pill colour on the live stock report.
                    </div>
                  </div>
                )}
              </div>

              {/* Custom attributes — driven by the selected category's custom_filters */}
              {showAttrs && (() => {
                const cat = categories.find((c) => c.slug === form.category_slug);
                const filters = cat?.custom_filters || [];
                if (filters.length === 0) {
                  return (
                    <div className="border-t border-neutral-200 pt-3">
                      <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Custom attributes</label>
                      <p className="text-[11px] text-neutral-500 mt-1">
                        No custom attributes defined for this category yet. Add them on the{' '}
                        <a
                          href="/admin/categories"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0078D4] font-semibold hover:underline"
                          data-testid="link-manage-custom-attrs"
                        >
                          Divisions page
                        </a>
                        {' '}— e.g. Brand, Country of Origin, Flavour — then come back here to set the value.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="border-t border-neutral-200 pt-3">
                    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Custom attributes</label>
                    <p className="text-[11px] text-neutral-500 mb-2">Set this product's value for each custom filter the division defines.</p>
                    <div className="grid grid-cols-2 gap-2">
                      {filters.map((f) => (
                        <div key={f.key}>
                          <div className="text-[11px] font-bold text-neutral-600 uppercase tracking-wide mb-1">{f.label}</div>
                          <select
                            value={(form.custom_attrs || {})[f.key] || ''}
                            onChange={(e) => setForm({ ...form, custom_attrs: { ...(form.custom_attrs || {}), [f.key]: e.target.value } })}
                            className="h-9 w-full rounded-md border border-neutral-300 bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
                            data-testid={`product-attr-${f.key}`}
                          >
                            <option value="">— None —</option>
                            {(f.options || []).map((o) => (
                              <option key={o.id || o.value || o.label} value={o.value || o.label}>{o.label || o.value}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {showTag && (
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Tag (optional)</label>
                <div className="mt-1 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {TAG_PRESETS.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm({ ...form, tag: form.tag === t ? '' : t })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                          form.tag === t
                            ? 'bg-[#0F172A] text-white border-[#0F172A]'
                            : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4]'
                        }`}
                        data-testid={`tag-preset-${t.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    <Input
                      value={form.tag}
                      onChange={(e) => setForm({ ...form, tag: e.target.value })}
                      placeholder="Or type a custom tag…"
                      data-testid="product-tag-input"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="color"
                        value={form.tag_color || '#0078D4'}
                        onChange={(e) => setForm({ ...form, tag_color: e.target.value })}
                        disabled={!form.tag}
                        className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer disabled:opacity-40"
                        title="Tag color"
                        data-testid="product-tag-color"
                      />
                      {form.tag_color && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, tag_color: '' })}
                          className="text-[10px] text-neutral-500 hover:text-neutral-900"
                        >
                          reset
                        </button>
                      )}
                    </div>
                  </div>
                  {form.tag && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-neutral-500">Preview:</span>
                      <span
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase text-white"
                        style={{
                          background:
                            form.tag_color ||
                            categories.find((c) => c.slug === form.category_slug)?.color ||
                            '#0078D4',
                        }}
                      >
                        {form.tag}
                      </span>
                      {!form.tag_color && (
                        <span className="text-[10px] text-neutral-400">Uses division color by default</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}
              {showImage && (
              <div>
                <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Image</label>
                <div className="mt-1">
                  <ImageUpload
                    value={form.image}
                    onChange={(url) => setForm({ ...form, image: url })}
                    onUploadingChange={setImageUploading}
                    bg={categories.find((c) => c.slug === form.category_slug)?.bg || '#F8FAFC'}
                    testId="product-image"
                  />
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">Leave empty to fall back to the division image.</p>
              </div>
              )}
              {showInStock && (
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <div className="text-sm font-semibold">Stock status</div>
                  <div className="text-xs text-neutral-500">"Coming Soon" shows the badge with Add-to-cart disabled</div>
                </div>
                <select
                  value={form.stock_status || (form.in_stock ? 'in_stock' : 'out_of_stock')}
                  onChange={(e) => {
                    const s = e.target.value;
                    setForm({ ...form, stock_status: s, in_stock: s === 'in_stock' });
                  }}
                  className="h-9 px-2 rounded-md border border-neutral-300 bg-white text-sm font-semibold text-neutral-700"
                  data-testid="product-stock-status"
                >
                  <option value="in_stock">In stock</option>
                  <option value="out_of_stock">Out of stock</option>
                  <option value="coming_soon">Coming Soon</option>
                </select>
              </div>
              )}
              {showVat && (
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <div className="text-sm font-semibold">VAT applicable (5%)</div>
                  <div className="text-xs text-neutral-500">Add 5% Oman VAT on invoice</div>
                </div>
                <Switch checked={form.vat_applicable} onCheckedChange={(v) => setForm({ ...form, vat_applicable: v })} className="data-[state=checked]:bg-[#0078D4]" />
              </div>
              )}
              <div className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {form.is_visible === false ? <EyeOff className="w-4 h-4 text-neutral-500" /> : <Eye className="w-4 h-4 text-[#0078D4]" />}
                    Show on storefront
                  </div>
                  <div className="text-xs text-neutral-500">
                    {form.is_visible === false
                      ? 'Hidden from /category pages, search and featured grids. Still editable in admin and counted in stock report.'
                      : 'Visible to customers in catalog, search and featured grids.'}
                  </div>
                </div>
                <Switch
                  checked={form.is_visible !== false}
                  onCheckedChange={(v) => setForm({ ...form, is_visible: v })}
                  className="data-[state=checked]:bg-[#0078D4]"
                  data-testid="product-visibility-switch"
                />
              </div>
            </form>
            <div className="p-5 border-t border-neutral-200 flex gap-2">
              <Button type="button" variant="outline" onClick={close} className="flex-1 h-11 rounded-full" data-testid="product-form-cancel">
                Cancel
              </Button>
              <Button onClick={save} disabled={saving || imageUploading} className="flex-1 h-11 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold" data-testid="product-form-submit">
                {saving ? 'Saving…' : imageUploading ? 'Waiting for image…' : editing === 'new' ? 'Create' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ProductFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        value={filters}
        onChange={setFilters}
        uomOptions={uomOptions}
        tagOptions={tagOptions}
      />

      <BulkProductUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        authHeaders={authHeaders}
        onUploaded={() => load()}
      />

      <BulkProductEdit
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedIds={selectedIds}
        categories={categories}
        products={products}
        authHeaders={authHeaders}
        onApplied={() => { clearSelection(); load(); }}
      />

      {/* Sticky bulk-action toolbar — appears as soon as ≥1 row is selected. */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-[#0F172A] text-white rounded-full shadow-2xl border border-neutral-700 flex items-center gap-2 pl-5 pr-2 py-2" data-testid="bulk-action-toolbar">
          <span className="text-sm font-semibold" data-testid="bulk-selection-count">
            {selectedIds.length} selected
          </span>
          <div className="w-px h-6 bg-neutral-600 mx-1" />
          {canBulkEdit && (
            <button
              onClick={() => setBulkEditOpen(true)}
              className="px-4 h-9 rounded-full bg-[#0078D4] hover:bg-[#0086EF] text-white font-semibold text-sm flex items-center gap-1.5"
              data-testid="bulk-edit-open"
            >
              <Wand2 className="w-4 h-4" /> Bulk edit
            </button>
          )}
          {canBulkDelete && (
            <button
              onClick={bulkDelete}
              className="px-4 h-9 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm flex items-center gap-1.5"
              data-testid="bulk-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button
            onClick={clearSelection}
            className="px-3 h-9 rounded-full hover:bg-neutral-700 text-neutral-200 text-sm"
            data-testid="bulk-clear"
            aria-label="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProducts;

/**
 * EditableText
 *
 * Click-to-edit text field used in the products table when editMode is on.
 *
 * UX choices:
 * - The cell shows the current value with a subtle dashed underline so admins
 *   know it's tappable. Tapping switches to a real <input> focused & selected.
 * - Saves on blur AND on Enter. Escape cancels without saving.
 * - The parent component's `onSave` handles the optimistic update + PUT, so
 *   this component stays purely presentational and reusable.
 * - `saving` shows a tiny spinner so admins know the value is in-flight when
 *   editing many cells in quick succession.
 */
const EditableText = ({ value, onSave, saving, type = 'text', step, placeholder, testId, prefix, className = '' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value ?? '')) onSave(draft);
  };
  const cancel = () => {
    setDraft(value ?? '');
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        {prefix && <span className="text-neutral-500 text-xs">{prefix}</span>}
        <input
          ref={inputRef}
          type={type}
          step={step}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          className={`h-8 px-2 rounded-md border border-[#0078D4] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4] w-full min-w-[80px] ${className}`}
          data-testid={testId}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`text-left min-h-[28px] inline-flex items-center gap-1 px-2 py-1 -mx-2 -my-1 rounded hover:bg-blue-50 border border-dashed border-transparent hover:border-[#0078D4] transition-colors ${className}`}
      data-testid={`${testId}-display`}
    >
      {prefix && <span className="text-neutral-500 text-xs">{prefix}</span>}
      <span className={value ? '' : 'text-neutral-400'}>{value || placeholder || '—'}</span>
      {saving && (
        <span className="w-2 h-2 rounded-full bg-[#0078D4] animate-pulse" aria-label="Saving…" />
      )}
    </button>
  );
};
